import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'

// 비로그인 공개 페이지: 비대면 서명(캔버스 직접 서명)
export default function SignClaimSignaturePage() {
  const { token } = useParams()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [done, setDone] = useState(false)

  const canvasRef = useRef(null)
  const ctxRef = useRef(null)
  const drawingRef = useRef(false)
  const lastRef = useRef({ x: 0, y: 0 })
  const hasInkRef = useRef(false)
  const docDrawingCleanupRef = useRef(null)
  const [hasInk, setHasInk] = useState(false)

  const removeDocDrawingListeners = () => {
    const fn = docDrawingCleanupRef.current
    if (fn) {
      fn()
      docDrawingCleanupRef.current = null
    }
  }

  const apiVersion = process.env.REACT_APP_API_VERSION || 'v1'
  const baseUrl = useMemo(() => {
    // 배포는 동일 오리진 기본 가정(nginx 프록시)
    return `/${apiVersion}`.replace(/\/{2,}/g, '/')
  }, [apiVersion])

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    const cssW = parent ? parent.clientWidth : canvas.clientWidth
    const cssH = Math.max(180, Math.min(320, Math.round((cssW || 320) * 0.55)))
    const dpr = window.devicePixelRatio || 1

    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctxRef.current = ctx
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 3
    ctx.strokeStyle = '#0f172a' // slate-900

    // background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, cssW, cssH)
  }, [])

  const clear = useCallback(() => {
    removeDocDrawingListeners()
    drawingRef.current = false
    const canvas = canvasRef.current
    const ctx = ctxRef.current
    if (!canvas || !ctx) return
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    hasInkRef.current = false
    setHasInk(false)
  }, [])

  const getPoint = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const cx = e.clientX ?? e.nativeEvent?.clientX ?? e.touches?.[0]?.clientX ?? 0
    const cy = e.clientY ?? e.nativeEvent?.clientY ?? e.touches?.[0]?.clientY ?? 0
    return { x: cx - rect.left, y: cy - rect.top }
  }

  const onPointerDown = (e) => {
    if (done || loading) return
    const canvas = canvasRef.current
    const ctx = ctxRef.current
    if (!canvas || !ctx) return
    try { e.preventDefault() } catch { }

    // 이전 드로잉 세션 정리 (연속 제스처)
    removeDocDrawingListeners()

    drawingRef.current = true
    const p = getPoint(e)
    lastRef.current = p
    try {
      canvas.setPointerCapture(e.pointerId)
    } catch {
      /* 일부 환경에서 캡처 실패해도 document 단계 리스너로 보완 */
    }

    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.25, p.y + 0.25)
    ctx.stroke()
    if (!hasInkRef.current) {
      hasInkRef.current = true
      setHasInk(true)
    }

    const onDocMove = (ev) => {
      if (!drawingRef.current) return
      const c = ctxRef.current
      if (!c) return
      try {
        ev.preventDefault()
      } catch {
        /* ignore */
      }
      const pt = getPoint(ev)
      const last = lastRef.current
      c.beginPath()
      c.moveTo(last.x, last.y)
      c.lineTo(pt.x, pt.y)
      c.stroke()
      lastRef.current = pt
      if (!hasInkRef.current) {
        hasInkRef.current = true
        setHasInk(true)
      }
    }

    const onDocEnd = (ev) => {
      if (!drawingRef.current) return
      drawingRef.current = false
      removeDocDrawingListeners()
      try {
        ev?.preventDefault?.()
      } catch {
        /* ignore */
      }
      try {
        if (ev?.pointerId != null && canvasRef.current) {
          canvasRef.current.releasePointerCapture(ev.pointerId)
        }
      } catch {
        /* ignore */
      }
    }

    docDrawingCleanupRef.current = () => {
      document.removeEventListener('pointermove', onDocMove)
      document.removeEventListener('pointerup', onDocEnd)
      document.removeEventListener('pointercancel', onDocEnd)
    }
    document.addEventListener('pointermove', onDocMove, { passive: false })
    document.addEventListener('pointerup', onDocEnd, { passive: false })
    document.addEventListener('pointercancel', onDocEnd, { passive: false })
  }

  useEffect(() => {
    setupCanvas()
    const onResize = () => {
      // 리사이즈 시 스케일 재설정(서명은 유지하지 않고 초기화: 모바일 회전 등에서 깨짐 방지)
      setupCanvas()
      clear()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [setupCanvas, clear])

  useEffect(() => {
    let ignore = false
    ; (async () => {
      if (!token) return
      setLoading(true)
      setError(null)
      setDone(false)
      try {
        const res = await axios.get(`${baseUrl}/claims/signatures/public/${token}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setData(payload)
        if (!ignore) {
          if (String(payload?.status || '').toUpperCase() === 'COMPLETED') {
            setDone(true)
          }
        }
      } catch (e) {
        // 410/404 등
        if (!ignore) setError('링크가 만료되었거나 유효하지 않습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [token, baseUrl])

  useEffect(() => () => removeDocDrawingListeners(), [])

  const submit = async () => {
    if (!token) return
    if (!hasInkRef.current) {
      setError('서명을 입력해 주세요.')
      return
    }
    const canvas = canvasRef.current
    if (!canvas) return
    setSubmitting(true)
    setError(null)
    try {
      const dataUrl = canvas.toDataURL('image/png')
      const res = await axios.post(`${baseUrl}/claims/signatures/public/${token}/complete`, { dataUrl })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setData(payload)
      setDone(true)
    } catch (e) {
      setError('서명 제출에 실패했습니다. 링크 만료 여부를 확인해 주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  const customerName = data?.customerName || ''
  const signerRole = String(data?.signerRole || '').toUpperCase()
  const roleLabel = signerRole === 'CONTRACT_HOLDER'
    ? '계약자'
    : signerRole === 'BENEFICIARY'
      ? '수익자'
      : '피보험자'
  const status = String(data?.status || '').toUpperCase()
  const isExpired = status === 'EXPIRED'

  return (
    <section className="max-w-xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{roleLabel} 서명</h1>
        <Link to="/login" className="text-sm text-slate-600 hover:underline">로그인</Link>
      </div>

      {loading && <div className="mt-6 text-sm text-slate-600">불러오는 중...</div>}
      {error && (
        <div className="mt-6 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="mt-6 space-y-4">
          <div className="rounded border bg-white p-4">
            <div className="text-sm text-slate-600">안내</div>
            <div className="mt-1 text-slate-900">
              {customerName ? (
                <span>
                  <span className="font-semibold">{customerName}</span> 님({roleLabel}), 아래 영역에 서명해 주세요.
                </span>
              ) : (
                <span>{roleLabel} 본인 확인 후 아래 영역에 서명해 주세요.</span>
              )}
            </div>
            <div className="mt-2 text-xs text-slate-500">
              모바일은 손가락, PC는 마우스로 직접 서명할 수 있습니다.
            </div>
          </div>

          <div className="rounded border bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">서명 입력</div>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
                onClick={clear}
                disabled={submitting || done}
              >
                지우기
              </button>
            </div>

            <div className="mt-3">
              <div className="rounded-lg border bg-slate-50 p-2">
                <canvas
                  ref={canvasRef}
                  className={`w-full cursor-crosshair rounded bg-white ${done ? 'opacity-60 pointer-events-none' : ''}`}
                  style={{ touchAction: 'none' }}
                  onPointerDown={onPointerDown}
                />
              </div>
              {!hasInk && !done && (
                <div className="mt-2 text-xs text-slate-500">
                  서명 영역이 비어 있습니다.
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                onClick={submit}
                disabled={submitting || done || isExpired}
              >
                {submitting ? '제출 중...' : done ? '완료됨' : '완료'}
              </button>
            </div>

            {done && (
              <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                서명이 완료되었습니다. 창을 닫아 주세요.
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

